import { ClientStatus } from '../types';

export interface WorkflowStepConfig {
    key: ClientStatus;
    displayName: string;
    description: string;
    status: ClientStatus | null; // Maps to actual client status, null for 'all'
    order: number;
    color: string;
    icon?: string;
}

export const WORKFLOW_STEPS: WorkflowStepConfig[] = [
    {
        key: 'draft',
        displayName: 'Draft',
        description: 'Awaiting financial statements',
        status: 'draft',
        order: 1,
        color: '#FBBF24', // yellow
        icon: '📝'
    },
    {
        key: 'financials',
        displayName: 'Financials',
        description: 'Financial statements uploaded',
        status: 'draft',
        order: 2,
        color: '#FBBF24', // yellow
        icon: '📝'
    },
    {
        key: 'lenders_submissions',
        displayName: 'Lenders & Submissions',
        description: 'Submitted to lenders',
        status: 'lenders_submissions',
        order: 3,
        color: '#A78BFA', // purple
        icon: '📤'
    },
    {
        key: 'funding_booking',
        displayName: 'Funding & Booking',
        description: 'Funding booking in progress',
        status: 'funding_booking',
        order: 4,
        color: '#6366F1', // indigo
        icon: '💰'
    },
    {
        key: 'completed',
        displayName: 'Final',
        description: 'Completed',
        status: 'completed',
        order: 5,
        color: '#10B981', // green
        icon: '✅'
    }
];

/**
 * Gets the order of a workflow step
 */
export function getClientStatusOrder(step: ClientStatus): number {
    return WORKFLOW_STEPS.find(s => s.key === step)?.order ?? 0;
}

/**
 * Checks if a workflow step is completed based on client status
 */
export function isWorkflowStepCompleted(status: ClientStatus, clientStatus: ClientStatus | undefined): boolean {
    if (!clientStatus) return false;
    
    const stepConfig = WORKFLOW_STEPS.find(s => s.key === status);
    if (!stepConfig) return false;
    
    // Check if current status order is >= step order
    const currentOrder = getClientStatusOrder(status);
    const stepOrder = getClientStatusOrder(clientStatus);
    
    return currentOrder >= stepOrder;
}

/**
 * Button text mapping for workflow progression buttons
 * Maps current stage to the button text that should be displayed
 */
export const WORKFLOW_BUTTON_TEXT: Record<ClientStatus, string> = {
    'draft': '',
    'financials': 'Submit to Lenders',
    'lenders_submissions': 'Move to Funding & Booking',
    'funding_booking': 'Mark as Completed',
    'completed': ''
};

/**
 * Checks if a workflow progression button should be shown for the given status
 */
export function shouldShowWorkflowButton(status: ClientStatus | undefined): boolean {
    if (!status) return false;
    return status === 'financials' || status === 'lenders_submissions' || status === 'funding_booking';
}

